import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGauge,
  faFilm,
  faAngleLeft,
  faUserShield,
  faAddressBook,
  faAddressCard,
  faCalendar,
  faGear,
  faDotCircle,
  faSignOutAlt,
  faImage,
  faKey,
} from '@fortawesome/free-solid-svg-icons';

import { useApp } from '../../providers/UserProvider';
import { ABILITIES } from '../../common/permissions';

import ImgFestival from '../../assets/images/festival-logo.png';
import UserPhoto from '../UserPhoto';

/**
 * Recuo dos submenus: eles ficam 20px a frente do menu-pai, para a hierarquia
 * ficar visivel de relance quando um menu esta aberto.
 */
const Sidebar = styled.aside`
  /*
   * Submenu recolhido: continua no lugar, mas com altura zero e o que passar
   * dela escondido. Abrir e fechar e a altura crescendo e diminuindo, e nao o
   * item aparecendo de uma vez. Fecha um pouco mais rapido do que abre, que e
   * como a gente espera que um menu se comporte.
   *
   * O navegador nao anima ate "a altura do conteudo", so ate um numero. Esse
   * numero e a altura real de cada submenu, medida pelo componente
   * (--fdr-submenu-height, ver measureSubmenus). Nao trocar por um teto fixo:
   * com 700px o submenu Configuracoes cresceu, passou do teto e o ultimo
   * item (Configuracoes Gerais) ficou cortado, escondido atras de Usuarios.
   * Sem a medida, o submenu abre sem limite (so nao desliza).
   */
  .nav-sidebar .nav-treeview {
    display: block;
    margin-left: 20px;
    max-height: 0;
    overflow: hidden;
    visibility: hidden;
    transition: max-height 0.25s ease-in, visibility 0.25s;
  }

  .nav-sidebar .menu-open > .nav-treeview {
    max-height: var(--fdr-submenu-height, none);
    visibility: visible;
    transition: max-height 0.4s ease-out, visibility 0s;
  }

  /*
   * Com o menu lateral recolhido, so aparecem os icones - e ai o recuo tira os
   * icones do submenu da coluna dos demais. Nesse estado o recuo sai, e volta
   * quando o menu esta aberto de novo, inclusive na expansao temporaria que o
   * mouse por cima provoca. E o mesmo tratamento que o AdminLTE da ao recuo
   * nativo dele.
   */
  body.sidebar-collapse & .nav-sidebar .nav-treeview {
    margin-left: 0;
  }

  body.sidebar-collapse &:hover .nav-sidebar .nav-treeview,
  body.sidebar-collapse &.sidebar-focused .nav-sidebar .nav-treeview {
    margin-left: 20px;
  }

  /*
   * Menu recolhido: o AdminLTE so torna invisiveis o nome e os links da
   * conta, que continuam ocupando as quatro linhas e afastam a foto da linha
   * de baixo. Aqui eles saem de vez, e voltam na expansao pelo mouse.
   */
  body.sidebar-collapse &:not(:hover):not(.sidebar-focused) .user-panel .info {
    display: none;
  }
`;

// Nome de quem está logado e, abaixo, os links da própria conta
const UserInfo = styled.div`
  span.name {
    display: block;
    color: #c2c7d0;
  }

  /* Modo escuro: o mesmo cinza, sem o azul (public/dist/css/fdr-dark-mode.css) */
  body.dark-mode & span.name {
    color: #c7c7c7;
  }

  a {
    display: block;
    font-size: 13px !important;
    line-height: 1.8;
    color: var(--fdr-primary-text) !important;
  }

  a:hover {
    text-decoration: underline;
  }

  a.logout,
  a.logout:hover {
    color: #fff !important;
  }

  a svg {
    width: 14px;
    margin-right: 5px;
  }
`;

function MainSidebar() {
  const {
    selectedMenu,
    selectedSubMenu,
    setSelectedMenu,
    setSelectedSubMenu,
    doLogout,
    authenticatedUser,
    isAdmin,
    isEditor,
    isProgramacao,
    can,
  } = useApp();

  // Editor e Administrador: o que o menu mostrava antes das permissões
  // (common/permissions.js). Os itens migrados perguntam can(ABILITIES.X).
  const legacy = isEditor() || isAdmin();

  /**
   * Abre o menu clicado e fecha o que estava aberto no mesmo instante, porque
   * so um nome cabe em `selectedMenu`. Clicar no menu ja aberto fecha ele.
   *
   * Quem manda no abre/fecha e aqui, nao o adminlte.js: o `data-widget`
   * saiu do <ul> justamente porque os dois brigavam pelo mesmo menu e o
   * jQuery, que roda depois, desfazia o que o React tinha acabado de fazer.
   */
  const handleMenuTree = (e, menu) => {
    e.preventDefault();
    setSelectedMenu(selectedMenu === menu ? null : menu);
  };

  /**
   * Altura real de cada submenu, o ponto final da animacao de abrir (ver o
   * comentario do .nav-treeview, no Sidebar). O scrollHeight mede o conteudo
   * inteiro mesmo com o submenu fechado (altura zero). Roda so ao abrir ou
   * fechar um menu, ao mudar o usuario (os itens dependem das permissoes), ao
   * passar o mouse no menu recolhido (ele se expande) e ao redimensionar a
   * janela: sao poucas leituras, sem custo perceptivel.
   */
  const sidebarRef = useRef(null);

  const measureSubmenus = useCallback(() => {
    if (!sidebarRef.current) {
      return;
    }

    sidebarRef.current.querySelectorAll('.nav-treeview').forEach(submenu => {
      submenu.style.setProperty(
        '--fdr-submenu-height',
        `${submenu.scrollHeight}px`,
      );
    });
  }, []);

  useLayoutEffect(measureSubmenus, [
    measureSubmenus,
    selectedMenu,
    authenticatedUser,
  ]);

  useEffect(() => {
    window.addEventListener('resize', measureSubmenus);

    return () => window.removeEventListener('resize', measureSubmenus);
  }, [measureSubmenus]);

  return (
    <Sidebar
      ref={sidebarRef}
      onMouseEnter={measureSubmenus}
      className="main-sidebar sidebar-dark-primary elevation-4"
    >
      <Link to="/dashboard" className="brand-link">
        <img
          src={ImgFestival}
          alt="FestivalDoRio"
          style={{ opacity: 0.8 }}
          className="brand-image img-circle elevation-3"
        />
        <span className="brand-text font-weight-light">Festival do Rio</span>
      </Link>

      <div className="sidebar">
        {authenticatedUser && (
          <div className="user-panel mt-3 pb-3 mb-3 d-flex">
            <div className="image">
              <UserPhoto
                user={authenticatedUser}
                size={34}
                round
                className="elevation-2"
              />
            </div>
            <div className="info">
              <UserInfo>
                <span className="name">{authenticatedUser.name}</span>
                <Link to="/alterar-imagem">
                  <FontAwesomeIcon icon={faImage} />
                  Alterar imagem
                </Link>
                <Link to="/alterar-senha">
                  <FontAwesomeIcon icon={faKey} />
                  Alterar senha
                </Link>
                <a href="/" onClick={doLogout} className="logout">
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  Sair
                </a>
              </UserInfo>
            </div>
          </div>
        )}

        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" role="menu">
            {can(ABILITIES.DASHBOARD) && (
              <li className="nav-item">
                <Link
                  to="/dashboard"
                  onClick={() => setSelectedMenu('dashboard')}
                  className={
                    selectedMenu === 'dashboard'
                      ? 'nav-link active'
                      : 'nav-link'
                  }
                >
                  <FontAwesomeIcon className="nav-icon" icon={faGauge} />
                  <p>Dashboard</p>
                </Link>
              </li>
            )}

            {isAdmin() && (
              <>
                <li className="nav-item">
                  <Link
                    to="/mostras"
                    onClick={() => setSelectedMenu('mostras')}
                    className={
                      selectedMenu === 'mostras'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                    <p>Mostras</p>
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/submostras"
                    onClick={() => setSelectedMenu('submostras')}
                    className={
                      selectedMenu === 'submostras'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                    <p>Sub-Mostras</p>
                  </Link>
                </li>
              </>
            )}

            {(legacy ||
              can(ABILITIES.PREMIERE_PRESS_LIST) ||
              can(ABILITIES.INTERNATIONAL_PRESS_LIST)) && (
              <>
                {(legacy || can(ABILITIES.PREMIERE_PRESS_LIST)) && (
                  <li
                    className={
                      selectedMenu === 'premiere'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/"
                      onClick={e => handleMenuTree(e, 'premiere')}
                      className={
                        selectedMenu === 'premiere'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                      <p>
                        Première Brasil
                        <FontAwesomeIcon className="right" icon={faAngleLeft} />
                      </p>
                    </a>
                    <ul className="nav nav-treeview">
                      {legacy && (
                        <>
                          <li className="nav-item">
                            <Link
                              to="/premiere/todos"
                              onClick={() =>
                                setSelectedSubMenu('premiere-todos')
                              }
                              className={
                                selectedSubMenu === 'premiere-todos'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Todos os Filmes</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/premiere/selecao"
                              onClick={() =>
                                setSelectedSubMenu('premiere-selecao')
                              }
                              className={
                                selectedSubMenu === 'premiere-selecao'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Seleção</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/premiere/materiais"
                              onClick={() =>
                                setSelectedSubMenu('premiere-materiais')
                              }
                              className={
                                selectedSubMenu === 'premiere-materiais'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Materiais</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/premiere/final"
                              onClick={() =>
                                setSelectedSubMenu('premiere-final')
                              }
                              className={
                                selectedSubMenu === 'premiere-final'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Final</p>
                            </Link>
                          </li>
                        </>
                      )}
                      {can(ABILITIES.PREMIERE_PRESS_LIST) && (
                        <li className="nav-item">
                          <Link
                            to="/premiere/imprensa"
                            onClick={() =>
                              setSelectedSubMenu('premiere-imprensa')
                            }
                            className={
                              selectedSubMenu === 'premiere-imprensa'
                                ? 'nav-link active'
                                : 'nav-link'
                            }
                          >
                            <FontAwesomeIcon
                              className="nav-icon"
                              icon={faDotCircle}
                            />
                            <p>Lista p/ Imprensa</p>
                          </Link>
                        </li>
                      )}
                      {legacy && (
                        <li className="nav-item">
                          <Link
                            to="/premiere/release"
                            onClick={() =>
                              setSelectedSubMenu('premiere-release')
                            }
                            className={
                              selectedSubMenu === 'premiere-release'
                                ? 'nav-link active'
                                : 'nav-link'
                            }
                          >
                            <FontAwesomeIcon
                              className="nav-icon"
                              icon={faDotCircle}
                            />
                            <p>Lista release</p>
                          </Link>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
                {(legacy || can(ABILITIES.INTERNATIONAL_PRESS_LIST)) && (
                  <li
                    className={
                      selectedMenu === 'internacional'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/"
                      onClick={e => handleMenuTree(e, 'internacional')}
                      className={
                        selectedMenu === 'internacional'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                      <p>
                        Coord. Internacional
                        <FontAwesomeIcon className="right" icon={faAngleLeft} />
                      </p>
                    </a>
                    <ul className="nav nav-treeview">
                      {legacy && (
                        <>
                          <li className="nav-item">
                            <Link
                              to="/internacional/todos"
                              onClick={() =>
                                setSelectedSubMenu('internacional-todos')
                              }
                              className={
                                selectedSubMenu === 'internacional-todos'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Todos os Filmes</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/internacional/screeners"
                              onClick={() =>
                                setSelectedSubMenu('internacional-screeners')
                              }
                              className={
                                selectedSubMenu === 'internacional-screeners'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Screeners</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/internacional/materiais"
                              onClick={() =>
                                setSelectedSubMenu('internacional-materiais')
                              }
                              className={
                                selectedSubMenu === 'internacional-materiais'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Materiais</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/internacional/final"
                              onClick={() =>
                                setSelectedSubMenu('internacional-final')
                              }
                              className={
                                selectedSubMenu === 'internacional-final'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Final</p>
                            </Link>
                          </li>
                        </>
                      )}
                      {can(ABILITIES.INTERNATIONAL_PRESS_LIST) && (
                        <li className="nav-item">
                          <Link
                            to="/internacional/imprensa"
                            onClick={() =>
                              setSelectedSubMenu('internacional-imprensa')
                            }
                            className={
                              selectedSubMenu === 'internacional-imprensa'
                                ? 'nav-link active'
                                : 'nav-link'
                            }
                          >
                            <FontAwesomeIcon
                              className="nav-icon"
                              icon={faDotCircle}
                            />
                            <p>Lista p/ Imprensa</p>
                          </Link>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
                {legacy && (
                  <li
                    className={
                      selectedMenu === 'catalogo'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/"
                      onClick={e => handleMenuTree(e, 'catalogo')}
                      className={
                        selectedMenu === 'catalogo'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                      <p>
                        Catálogo
                        <FontAwesomeIcon className="right" icon={faAngleLeft} />
                      </p>
                    </a>
                    <ul className="nav nav-treeview">
                      <li className="nav-item">
                        <Link
                          to="/lista-catalogo-premiere-brasil"
                          onClick={() =>
                            setSelectedSubMenu('list-catalogo-premiere-brasil')
                          }
                          className={
                            selectedSubMenu === 'list-catalogo-premiere-brasil'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Première Brasil</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/lista-catalogo-filmes-internacionais"
                          onClick={() =>
                            setSelectedSubMenu('list-catalogo-internacionais')
                          }
                          className={
                            selectedSubMenu === 'list-catalogo-internacionais'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Internacionais</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/todos-filmes-catalogo-premiere-brasil"
                          onClick={() =>
                            setSelectedSubMenu('list-catalogo-todos-pb')
                          }
                          className={
                            selectedSubMenu === 'list-catalogo-todos-pb'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Todos os Filmes PB</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/todos-filmes-catalogo-internacional"
                          onClick={() =>
                            setSelectedSubMenu('list-catalogo-todos-int')
                          }
                          className={
                            selectedSubMenu === 'list-catalogo-todos-int'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Todos os Filmes INT</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/catalogo/conteudo/revista"
                          onClick={() => setSelectedSubMenu('catalogo-revista')}
                          className={
                            selectedSubMenu === 'catalogo-revista'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Conteúdo p/ Revista</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}
              </>
            )}

            {(isProgramacao() || isEditor() || isAdmin()) && (
              <>
                <li className="nav-item">
                  <Link
                    to="/projecao"
                    onClick={() => setSelectedMenu('projecao')}
                    className={
                      selectedMenu === 'projecao'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                    <p>Projeção</p>
                  </Link>
                </li>
                <li
                  className={
                    selectedMenu === 'programacao'
                      ? 'nav-item menu-is-opening menu-open'
                      : 'nav-item'
                  }
                >
                  <a
                    href="/"
                    onClick={e => handleMenuTree(e, 'programacao')}
                    className={
                      selectedMenu === 'programacao'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faFilm} />
                    <p>
                      Programação
                      <FontAwesomeIcon className="right" icon={faAngleLeft} />
                    </p>
                  </a>
                  <ul className="nav nav-treeview">
                    <li className="nav-item">
                      <Link
                        to="/cinemas"
                        onClick={() =>
                          setSelectedSubMenu('programacao-cinemas')
                        }
                        className={
                          selectedSubMenu === 'programacao-cinemas'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Cinemas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/programacao/pelicula"
                        onClick={() =>
                          setSelectedSubMenu('programacao-pelicula')
                        }
                        className={
                          selectedSubMenu === 'programacao-pelicula'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Películas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/programacao/programacoes"
                        onClick={() =>
                          setSelectedSubMenu('programacao-programacoes')
                        }
                        className={
                          selectedSubMenu === 'programacao-programacoes'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Sessões nos Cinemas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/programacao/renda"
                        onClick={() => setSelectedSubMenu('programacao-renda')}
                        className={
                          selectedSubMenu === 'programacao-renda'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Renda</p>
                      </Link>
                    </li>
                  </ul>
                </li>
              </>
            )}

            {(legacy || can(ABILITIES.PRESS_CREDENTIALS)) && (
              <>
                {(legacy || can(ABILITIES.PRESS_CREDENTIALS)) && (
                  <li
                    className={
                      selectedMenu === 'credenciamento'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/"
                      onClick={e => handleMenuTree(e, 'credenciamento')}
                      className={
                        selectedMenu === 'credenciamento'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon
                        className="nav-icon"
                        icon={faAddressCard}
                      />
                      <p>
                        Credenciamento
                        <FontAwesomeIcon className="right" icon={faAngleLeft} />
                      </p>
                    </a>
                    <ul className="nav nav-treeview">
                      {legacy && (
                        <>
                          <li className="nav-item">
                            <Link
                              to="/credenciamento/geral"
                              onClick={() =>
                                setSelectedSubMenu('credenciamento-geral')
                              }
                              className={
                                selectedSubMenu === 'credenciamento-geral'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Convidados Geral</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/credenciamento/premiere-brasil"
                              onClick={() =>
                                setSelectedSubMenu('credenciamento-premiere')
                              }
                              className={
                                selectedSubMenu === 'credenciamento-premiere'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Convidados PB</p>
                            </Link>
                          </li>
                        </>
                      )}
                      {can(ABILITIES.PRESS_CREDENTIALS) && (
                        <li className="nav-item">
                          <Link
                            to="/credenciamento/imprensa"
                            onClick={() =>
                              setSelectedSubMenu('credenciamento-imprensa')
                            }
                            className={
                              selectedSubMenu === 'credenciamento-imprensa'
                                ? 'nav-link active'
                                : 'nav-link'
                            }
                          >
                            <FontAwesomeIcon
                              className="nav-icon"
                              icon={faDotCircle}
                            />
                            <p>Imprensa</p>
                          </Link>
                        </li>
                      )}
                      {legacy && (
                        <>
                          <li className="nav-item">
                            <Link
                              to="/credenciamento/equipe"
                              onClick={() =>
                                setSelectedSubMenu('credenciamento-equipe')
                              }
                              className={
                                selectedSubMenu === 'credenciamento-equipe'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Equipe</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/credenciamento/riomarket"
                              onClick={() =>
                                setSelectedSubMenu('credenciamento-riomarket')
                              }
                              className={
                                selectedSubMenu === 'credenciamento-riomarket'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Riomarket</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/credenciamento/acesso-especial"
                              onClick={() =>
                                setSelectedSubMenu('credenciamento-acesso')
                              }
                              className={
                                selectedSubMenu === 'credenciamento-acesso'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Acesso Especial</p>
                            </Link>
                          </li>
                          <li className="nav-item">
                            <Link
                              to="/convidados/geral"
                              onClick={() =>
                                setSelectedSubMenu('convidados-geral')
                              }
                              className={
                                selectedSubMenu === 'convidados-geral'
                                  ? 'nav-link active'
                                  : 'nav-link'
                              }
                            >
                              <FontAwesomeIcon
                                className="nav-icon"
                                icon={faDotCircle}
                              />
                              <p>Lista Geral</p>
                            </Link>
                          </li>
                        </>
                      )}
                    </ul>
                  </li>
                )}
                {legacy && (
                  <li
                    className={
                      selectedMenu === 'convidados'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/"
                      onClick={e => handleMenuTree(e, 'convidados')}
                      className={
                        selectedMenu === 'convidados'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon
                        className="nav-icon"
                        icon={faAddressBook}
                      />
                      <p>
                        Hospitalidade
                        <FontAwesomeIcon className="right" icon={faAngleLeft} />
                      </p>
                    </a>
                    <ul className="nav nav-treeview">
                      <li className="nav-item">
                        <Link
                          to="/convidados/hospedagens"
                          onClick={() =>
                            setSelectedSubMenu('convidados-hospedagens')
                          }
                          className={
                            selectedSubMenu === 'convidados-hospedagens'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Hospedagens</p>
                        </Link>
                      </li>
                      <li className="nav-item">
                        <Link
                          to="/convidados/viagens"
                          onClick={() =>
                            setSelectedSubMenu('convidados-viagens')
                          }
                          className={
                            selectedSubMenu === 'convidados-viagens'
                              ? 'nav-link active'
                              : 'nav-link'
                          }
                        >
                          <FontAwesomeIcon
                            className="nav-icon"
                            icon={faDotCircle}
                          />
                          <p>Viagens</p>
                        </Link>
                      </li>
                    </ul>
                  </li>
                )}
                {legacy && (
                  <li
                    className={
                      selectedMenu === 'eventos'
                        ? 'nav-item menu-is-opening menu-open'
                        : 'nav-item'
                    }
                  >
                    <a
                      href="/convidados/eventos"
                      className={
                        selectedMenu === 'eventos'
                          ? 'nav-link active'
                          : 'nav-link'
                      }
                    >
                      <FontAwesomeIcon className="nav-icon" icon={faCalendar} />
                      <p>Eventos</p>
                    </a>
                  </li>
                )}
              </>
            )}

            {isAdmin() && (
              <>
                <li
                  className={
                    selectedMenu === 'configuracoes'
                      ? 'nav-item menu-is-opening menu-open'
                      : 'nav-item'
                  }
                >
                  <a
                    href="/"
                    onClick={e => handleMenuTree(e, 'configuracoes')}
                    className={
                      selectedMenu === 'configuracoes'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faGear} />
                    <p>
                      Configurações
                      <FontAwesomeIcon className="right" icon={faAngleLeft} />
                    </p>
                  </a>
                  <ul className="nav nav-treeview">
                    <li className="nav-item">
                      <Link
                        to="/hoteis"
                        onClick={() => setSelectedSubMenu('hoteis')}
                        className={
                          selectedSubMenu === 'hoteis'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Hotéis</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/categorias"
                        onClick={() => setSelectedSubMenu('categorias')}
                        className={
                          selectedSubMenu === 'categorias'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Categorias da PB</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/categorias-convidado"
                        onClick={() =>
                          setSelectedSubMenu('categorias-convidado')
                        }
                        className={
                          selectedSubMenu === 'categorias-convidado'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Categorias de Conv.</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/vinhetas"
                        onClick={() => setSelectedSubMenu('vinhetas')}
                        className={
                          selectedSubMenu === 'vinhetas'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Vinhetas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/telas-de-descanso"
                        onClick={() => setSelectedSubMenu('telas-de-descanso')}
                        className={
                          selectedSubMenu === 'telas-de-descanso'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Telas de descanso</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/tipos-de-ingresso"
                        onClick={() => setSelectedSubMenu('tipos-de-ingresso')}
                        className={
                          selectedSubMenu === 'tipos-de-ingresso'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Tipos de Ingresso</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/idiomas"
                        onClick={() => setSelectedSubMenu('idiomas')}
                        className={
                          selectedSubMenu === 'idiomas'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Idiomas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/diretores"
                        onClick={() => setSelectedSubMenu('diretores')}
                        className={
                          selectedSubMenu === 'diretores'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Diretores</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/elenco"
                        onClick={() => setSelectedSubMenu('elenco')}
                        className={
                          selectedSubMenu === 'elenco'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Elenco</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/roteiristas"
                        onClick={() => setSelectedSubMenu('roteiristas')}
                        className={
                          selectedSubMenu === 'roteiristas'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Roteiristas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/distribuidoras"
                        onClick={() => setSelectedSubMenu('distribuidoras')}
                        className={
                          selectedSubMenu === 'distribuidoras'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Print Sources</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/distribuidoras-atribuidas"
                        onClick={() =>
                          setSelectedSubMenu('distribuidoras-atribuidas')
                        }
                        className={
                          selectedSubMenu === 'distribuidoras-atribuidas'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Distribuidoras Atribuídas</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/produtoras"
                        onClick={() => setSelectedSubMenu('produtoras')}
                        className={
                          selectedSubMenu === 'produtoras'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Produtoras</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/produtores"
                        onClick={() => setSelectedSubMenu('produtores')}
                        className={
                          selectedSubMenu === 'produtores'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Produtores</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/fotografos"
                        onClick={() => setSelectedSubMenu('fotografos')}
                        className={
                          selectedSubMenu === 'fotografos'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Fotógrafos</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/apoios"
                        onClick={() => setSelectedSubMenu('apoios')}
                        className={
                          selectedSubMenu === 'apoios'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Apoios</p>
                      </Link>
                    </li>
                    <li className="nav-item">
                      <Link
                        to="/configuracoes/gerais"
                        onClick={() => setSelectedSubMenu('gerais')}
                        className={
                          selectedSubMenu === 'gerais'
                            ? 'nav-link active'
                            : 'nav-link'
                        }
                      >
                        <FontAwesomeIcon
                          className="nav-icon"
                          icon={faDotCircle}
                        />
                        <p>Configurações Gerais</p>
                      </Link>
                    </li>
                  </ul>
                </li>
                <li className="nav-item">
                  <Link
                    to="/usuarios"
                    onClick={() => setSelectedMenu('usuarios')}
                    className={
                      selectedMenu === 'usuarios'
                        ? 'nav-link active'
                        : 'nav-link'
                    }
                  >
                    <FontAwesomeIcon className="nav-icon" icon={faUserShield} />
                    <p>Usuários</p>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>
      </div>
    </Sidebar>
  );
}

export default MainSidebar;
